// Landing page content configuration
// Centralized content model for the WeRelay landing page

export const landingContent = {
  brand: {
    name: "WeRelay",
    logoLight: "/brand/logo_with_text.png",
    logoMark: "/brand/black_with_trans_bg.png",
  },
  links: {
    login: "https://app.werelayapp.com/login",
    docs: "https://werelayapp.com/docs",
    contact: "https://werelayapp.com/contact",
    sales: "https://werelayapp.com/contact",
    api: "https://werelayapp.com/docs",
    privacy: "/privacy_policy",
    terms: "https://werelayapp.com/terms",
  },
  hero: {
    headline: "WhatsApp Business Messaging, Built for Reliable Communication",
    subheadline:
      "Power your WhatsApp messaging — transactional, promotional, and conversational — through a single, robust platform designed for dependable delivery and clean integrations.",
    description:
      "WeRelay provides a unified messaging layer that simplifies WhatsApp communication for growing businesses and established teams alike.",
    primaryCta: "Login",
    secondaryCta: "View Documentation",
  },
  trustStrip: {
    items: [
      "Built on official WhatsApp Business infrastructure",
      "API-first, webhook-driven architecture",
      "Designed for consistent delivery and operational clarity",
    ],
  },
  why: {
    title: "A Practical Approach to WhatsApp Business Messaging",
    description:
      "WhatsApp is a critical customer communication channel, but managing it at scale introduces operational complexity — onboarding, message handling, retries, observability, and system coordination. WeRelay addresses these challenges by providing a stable, predictable messaging platform that integrates cleanly into existing business systems.",
  },
  capabilities: {
    title: "Messaging Capabilities",
    description:
      "WeRelay supports a broad range of WhatsApp Business communication needs:",
    items: [
      {
        title: "Transactional messaging",
        description:
          "Alerts, OTPs, order confirmations, service notifications",
      },
      {
        title: "Promotional messaging",
        description:
          "Outbound campaigns, announcements, customer re-engagement",
      },
      {
        title: "Conversational messaging",
        description:
          "Two-way interactions for support and service workflows",
      },
      {
        title: "Operational communication",
        description: "Reminders, updates, and system-driven messages",
      },
    ],
    note: "All message flows are managed through a single platform and API.",
  },
  howItWorks: {
    title: "How the Platform Works",
    description: "A clear and controlled message delivery pipeline.",
    steps: [
      {
        title: "Account & Sender Configuration",
        description:
          "Connect your WhatsApp Business sender with guided onboarding support.",
      },
      {
        title: "Message Submission",
        description:
          "Send messages from applications, CRMs, or internal tools using REST APIs.",
      },
      {
        title: "Message Processing",
        description:
          "WeRelay manages queuing, ordering, and retries to ensure consistent delivery under load.",
      },
      {
        title: "Delivery Feedback",
        description:
          "Receive delivery status and failure events via webhooks in real time.",
      },
    ],
  },
  apiPreview: {
    title: "APIs Designed for Long-Term Use",
    description:
      "RESTful APIs with predictable JSON payloads. Webhook-based event delivery. Minimal endpoints to simplify maintenance. Compatible with backend systems, CRMs, and automation platforms. Built for teams that value stability and clarity.",
    codeLanguage: "curl",
    code:
      "curl -X POST https://api.werelayapp.com/v1/messages \\\n  -H \"Authorization: Bearer <API_KEY>\" \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\n    \"to\": \"+14155552671\",\n    \"type\": \"template\",\n    \"template\": \"service_notification\",\n    \"variables\": {\n      \"reference_id\": \"18421\"\n    }\n  }'",
  },
  reliability: {
    title: "Consistent Delivery at Scale",
    description:
      "WeRelay is designed to support high-volume and business-critical messaging workloads.",
    points: [
      "Queue-based processing to manage traffic spikes",
      "Automatic retries for transient delivery issues",
      "At-least-once delivery semantics",
      "Reliable webhook performance for downstream systems",
    ],
    note: "The platform is engineered to behave predictably as usage grows.",
  },
  security: {
    title: "Security and Operational Control",
    description:
      "WeRelay is built to integrate into environments with internal review, compliance, and governance processes.",
    points: [
      "Secure API authentication",
      "Structured message handling and visibility",
      "Clear separation of messaging workflows",
      "Delivery and failure transparency through events",
    ],
  },
  pricing: {
    title: "Commercial Model",
    description:
      "Pricing is usage-based and aligned with WhatsApp messaging volume.",
    points: [
      "Transparent pricing structure",
      "Scales with message usage",
      "No unnecessary platform add-ons",
    ],
    cta: "Contact Sales",
  },
  roadmap: {
    title: "Platform Roadmap",
    items: [
      "Web-based interface for message visibility and diagnostics",
      "Template management and lifecycle controls",
      "Campaign tooling for outbound messaging",
      "Conversation tooling for support workflows",
      "Operational monitoring and alerts",
    ],
  },
  faq: {
    title: "FAQ",
    items: [
      {
        q: "What types of WhatsApp messaging does WeRelay support?",
        a: "WeRelay supports transactional, promotional, and conversational WhatsApp Business messaging.",
      },
      {
        q: "Is WeRelay suitable for larger teams and systems?",
        a: "Yes. The platform is designed to integrate cleanly into both growing and established technology stacks.",
      },
      {
        q: "Can WeRelay integrate with existing tools?",
        a: "Yes. The API-first design allows integration with CRMs, ERPs, backend services, and automation platforms.",
      },
    ],
  },
  footer: {
    tagline: "WhatsApp Business Messaging Platform",
    productLinks: [
      { label: "Login", href: "login" },
      { label: "API", href: "api" },
      { label: "Documentation", href: "docs" },
    ],
    companyLinks: [
      { label: "Contact", href: "contact" },
      { label: "Privacy", href: "privacy" },
      { label: "Terms", href: "terms" },
    ],
    copyright: "© 2025 WeRelayApp.",
    tagline2: "Designed for reliability, clarity, and long-term use.",
  },
};

